import React from 'react'
import classNames from 'classnames'
import PropTypes from 'prop-types'

const BUTTON_CLASSES = [
  'pointer transition no-outline font-size-large radius-normal',
  'flexbox flexbox-align-center flexbox-justify-center',
]
const DISABLED_DEFAULT_CLASSES =
  'cursor-forbidden background-shade-40 shade-100 border-none'

export function Base({
  children,
  slim,
  className,
  style = undefined,
  disabled,
  ...props
}) {
  const _className = classNames(
    BUTTON_CLASSES,
    'font-weight-bold',
    slim
      ? 'padding-vertical-small padding-horizontal-normal'
      : 'padding-vertical-normal padding-horizontal-medium',
    className
  )

  const isLink = Boolean(props.href)
  const isBlankTarget = props.target && props.target === '_blank'

  return React.createElement(
    isLink ? 'a' : 'button',
    {
      className: _className,
      style,
      disabled,
      ...(isLink && isBlankTarget && { rel: 'noopener noreferrer' }),
      ...props,
    },
    children
  )
}

Base.propTypes = {
  children: PropTypes.node,
  slim: PropTypes.bool,
  className: PropTypes.string,
  style: PropTypes.object,
}

function makeButtonComponent({
  classes = '',
  classesDisabled = DISABLED_DEFAULT_CLASSES,
  variationStyle = {},
  variationStyleDisabled = {},
}) {
  return ({
    children,
    className = undefined,
    style = {},
    icon = undefined,
    ...props
  }) => {
    const _style = props.disabled ? variationStyleDisabled : variationStyle
    const _classes = props.disabled ? classesDisabled : classes
    return (
      <Base
        className={classNames(className, _classes)}
        style={{ ..._style, ...style }}
        {...props}
      >
        {icon && <div className="padding-right-normal">{icon}</div>}
        {children}
      </Base>
    )
  }
}

export const Primary = makeButtonComponent({
  classes: [
    'background-primary border-none',
    'inset-shadow-on-hover',
    'background-primary-dark-on-active',
    'shade-100-important',
  ],
})

export const Secondary = makeButtonComponent({
  classes: [
    'background-shade-100',
    'shade-20 border-shade-30',
    'shade-10-on-hover border-shade-30-on-hover border-small background-shade-50-on-active',
  ],
  classesDisabled: 'shade-30 background-shade-45 border-shade-45',
  variationStyle: {
    boxShadow: 'inset 0px -2px 2px rgba(0, 0, 0, 0.06)',
  },
  variationStyleDisabled: { boxShadow: 'none' },
})

export const Tertiary = makeButtonComponent({
  classes: [
    'background-transparent',
    'shade-20 border-none',
    'shade-10-on-hover',
    'background-shade-50-on-hover',
    'background-shade-45-on-active',
  ],
  classesDisabled: 'shade-30 background-transparent border-none',
})

export const Danger = makeButtonComponent({
  classes: [
    'red background-shade-100 border-small border-red',
    'shade-100-on-hover background-red-on-hover',
  ],
})

export const Disabled = makeButtonComponent({
  classes: 'shade-30 background-shade-45 border-shade-45',
  variationStyle: {
    boxShadow: '0 2px 4px 0 rgba(0,0,0,0.05)',
  },
})

export const Login = makeButtonComponent({
  classes: [
    'shade-10 border-primary-on-hover',
    'border-small border-shade-30 background-shade-100 transition',
  ],
})

export const Success = makeButtonComponent({
  classes: 'shade-100 background-green border-green',
})

export const Banner = makeButtonComponent({
  classes: 'red background-shade-100 border-shade-100',
})

export const BannerReminder = makeButtonComponent({
  classes: 'shade-100 bold background-primary border-shade-100 border-medium',
  variationStyle: { fontWeight: 500 },
})

export const OnboardingPopover = makeButtonComponent({
  classes: 'shade-100-important opacity-large-on-hover transition',
  variationStyle: {
    background: 'rgba(0, 0, 0, .4)',
    border: 'none',
  },
})

export default {
  Banner,
  Base,
  Primary,
  Secondary,
  Tertiary,
  Danger,
  Login,
  OnboardingPopover,
}
